import { type RefObject, useEffect, useRef, useState } from "react";

/** Slack (px) to counter sub-pixel layout/measurement rounding at exact-fit boundaries. */
const SUBPIXEL_TOLERANCE = 0.5;

interface Options {
  /** Total number of items to lay out. */
  count: number;
  /** Minimum width of a single column, in px. Columns stretch to fill beyond this. */
  minItemWidth: number;
  /** Gap between columns, in px (matches the grid's `gap`). */
  gap?: number;
}

interface Result<T extends HTMLElement> {
  containerRef: RefObject<T | null>;
  /** How many items to render inline; the remainder belong in the dropdown. */
  visibleCount: number;
  /** Total grid columns to render (visible items, plus the trigger when overflowing). */
  columns: number;
  /** Whether there are items that didn't fit inline. */
  hasOverflow: boolean;
}

/**
 * Watches a container's width and reports how many equal-width columns fit,
 * given a minimum column width. Columns are expected to stretch (`1fr`) to
 * fill the container, so the caller never has empty trailing space.
 *
 * When items overflow, one column is reserved for an overflow trigger.
 *
 * The hook is headless: rendering the grid, items, and trigger is up to the
 * caller. Use `columns` for the grid template and `visibleCount` to slice the
 * items shown inline.
 */
export function useResponsiveCount<T extends HTMLElement = HTMLDivElement>({
  count,
  minItemWidth,
  gap = 0,
}: Options): Result<T> {
  const containerRef = useRef<T>(null);
  const [visibleCount, setVisibleCount] = useState(count);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const compute = (width: number) => {
      // n columns occupy: n * minItemWidth + (n - 1) * gap <= width
      // => n <= (width + gap) / (minItemWidth + gap)
      const available = width + gap + SUBPIXEL_TOLERANCE;
      const maxColumns = Math.max(
        1,
        Math.floor(available / (minItemWidth + gap)),
      );

      // Everything fits: show all items, no trigger needed.
      if (count <= maxColumns) {
        setVisibleCount(count);
        return;
      }

      // Overflow: reserve the last column for the trigger.
      setVisibleCount(Math.max(0, maxColumns - 1));
    };

    const observer = new ResizeObserver(([entry]) => {
      if (entry) compute(entry.contentRect.width);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [count, minItemWidth, gap]);

  const hasOverflow = visibleCount < count;

  return {
    containerRef,
    visibleCount,
    columns: hasOverflow ? visibleCount + 1 : visibleCount,
    hasOverflow,
  };
}
